/** A Ribbon is a visual element that you can assign to products to highlight them on your site. */
export interface Ribbon {
  /**
   * Ribbon ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the ribbon is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the ribbon.
   *
   * Ignored when creating a ribbon.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the ribbon was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the ribbon was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Ribbon name.
   * @minLength 1
   * @maxLength 30
   */
  name?: string;
  /**
   * Number of products this ribbon is assigned to.
   * > **Note:** Returned only when you pass `"ASSIGNED_PRODUCT_COUNT"` to the `fields` array in Ribbon API requests.
   * @readonly
   */
  assignedProductCount?: number | null;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface CreateRibbonRequest {
  /** Ribbon to create. */
  ribbon: Ribbon;
}

export interface CreateRibbonResponse {
  /** Created ribbon. */
  ribbon?: Ribbon;
}

export interface GetRibbonRequest {
  /**
   * Ribbon ID.
   * @format GUID
   */
  ribbonId: string;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export enum RequestedFields {
  ASSIGNED_PRODUCT_COUNT = 'ASSIGNED_PRODUCT_COUNT',
}

/** @enumType */
export type RequestedFieldsWithLiterals =
  | RequestedFields
  | 'ASSIGNED_PRODUCT_COUNT';

export interface GetRibbonResponse {
  /** Ribbon. */
  ribbon?: Ribbon;
}

export interface UpdateRibbonRequest {
  /** Ribbon to update. */
  ribbon: Ribbon;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface UpdateRibbonResponse {
  /** Updated Ribbon. */
  ribbon?: Ribbon;
}

export interface DeleteRibbonRequest {
  /**
   * Ribbon ID.
   * @format GUID
   */
  ribbonId: string;
}

export interface DeleteRibbonResponse {}

export interface QueryRibbonsRequest {
  /** Query options. */
  query?: CursorQuery;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 4
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  /** Ascending order. */
  ASC = 'ASC',
  /** Descending order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryRibbonsResponse {
  /**
   * List of ribbons.
   * @minSize 1
   * @maxSize 100
   */
  ribbons?: Ribbon[];
  /** Details on the paged set of results returned. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkCreateRibbonsRequest {
  /**
   * Ribbons to create.
   * @minSize 1
   * @maxSize 100
   */
  ribbons: Ribbon[];
  /**
   * Whether to return the full created ribbon entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface BulkCreateRibbonsResponse {
  /**
   * Ribbons created by bulk action.
   * @maxSize 100
   */
  results?: V3BulkRibbonResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface V3BulkRibbonResult {
  /** Bulk action metadata for ribbon. */
  itemMetadata?: ItemMetadata;
  /**
   * Full ribbon entity.
   *
   * Returned only if `returnEntity: true` is passed in the request.
   */
  item?: Ribbon;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateRibbonsRequest {
  /**
   * List of ribbons to update.
   * @minSize 1
   * @maxSize 100
   */
  ribbons: MaskedRibbon[];
  /**
   * Whether to return the full updated ribbon entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface MaskedRibbon {
  /** Ribbon to update. */
  ribbon?: Ribbon;
  /** Explicit list of fields to update. */
  fieldMask?: string[];
}

export interface BulkUpdateRibbonsResponse {
  /**
   * Ribbons updated by bulk action.
   * @maxSize 100
   */
  results?: V3BulkRibbonResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface GetOrCreateRibbonRequest {
  /**
   * Ribbon name to retrieve or create.
   * @minLength 1
   * @maxLength 30
   */
  ribbonName: string;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface GetOrCreateRibbonResponse {
  /** Ribbon. */
  ribbon?: Ribbon;
}

export interface BulkGetOrCreateRibbonsRequest {
  /**
   * Ribbon names to retrieve or create.
   * @minLength 1
   * @maxLength 30
   * @minSize 1
   * @maxSize 100
   */
  ribbonNames: string[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface BulkGetOrCreateRibbonsResponse {
  /**
   * Ribbons retrieved or created by bulk action.
   * @maxSize 100
   */
  results?: V3BulkRibbonResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkGetOrCreateRibbonsForMigrationRequest {
  /**
   * Ribbons to get or create (id and name are used; id is preserved).
   * @minSize 1
   * @maxSize 100
   */
  ribbons?: Ribbon[];
  /**
   * Fields to include in the response. Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface BulkGetOrCreateRibbonsForMigrationResponse {
  /**
   * Per-ribbon results.
   * @maxSize 100
   */
  results?: V3BulkRibbonResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteRibbonsRequest {
  /**
   * IDs of ribbons to delete.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  ribbonIds: string[];
}

export interface BulkDeleteRibbonsResponse {
  /**
   * Ribbons deleted by bulk action.
   * @maxSize 100
   */
  results?: BulkRibbonResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkRibbonResult {
  /** Bulk action metadata for ribbon. */
  itemMetadata?: ItemMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface RecloneSiteRequest {
  /** @format GUID */
  cloneFrom?: string;
}

export interface RecloneSiteResponse {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
